import { useId, useState, type ReactNode } from 'react';
import styled from 'styled-components';

import { IconChevronDown } from '../icons';
import { useListFiltersCollapsibleViewport } from '../../../hooks/useListFiltersCollapsibleViewport';

const ToggleRow = styled.div`
  margin-bottom: ${({ theme }) => theme.space[1]}px;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 40px;
  padding: 0 ${({ theme }) => theme.space[2]}px;
  gap: ${({ theme }) => theme.space[1]}px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-family: inherit;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.main};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.highlight.blue25};
  }
`;

const ToggleLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const ActiveBadge = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary.main};
`;

const ChevronWrap = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
`;

export type CollapsibleListFiltersProps = {
  children: ReactNode;
  /** Число ненулевых фильтров; показывается в свёрнутой строке на мобильном. */
  activeFilterCount?: number;
};

/**
 * На узком экране (≤ theme.breakpoint.listFiltersCollapse) фильтры по умолчанию
 * скрыты за кнопкой «Фильтры», чтобы таблица занимала основную высоту viewport.
 * На широком экране дети рендерятся без обёртки.
 */
export function CollapsibleListFilters({
  children,
  activeFilterCount = 0,
}: CollapsibleListFiltersProps) {
  const collapseByViewport = useListFiltersCollapsibleViewport();
  const panelId = useId();
  const [expanded, setExpanded] = useState(false);

  if (!collapseByViewport) {
    return <>{children}</>;
  }

  const countLabel =
    activeFilterCount > 0 ? (
      <ActiveBadge aria-hidden>({activeFilterCount})</ActiveBadge>
    ) : null;

  return (
    <div>
      <ToggleRow>
        <ToggleButton
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((v) => !v)}
        >
          <ToggleLabel>
            Фильтры
            {countLabel}
          </ToggleLabel>
          <ChevronWrap $expanded={expanded} aria-hidden>
            <IconChevronDown />
          </ChevronWrap>
        </ToggleButton>
      </ToggleRow>
      {expanded ? <div id={panelId}>{children}</div> : null}
    </div>
  );
}
